// Shared TSL noise helpers. Extracted from LavaMaterial / SpaceMaterial so
// the new layered lava + solar-spurt shaders can compose the same building
// blocks without each inlining their own copy.
//
// All functions are pure TSL Fn() wrappers. They read like GLSL but compile
// to the WebGPU backend via three/webgpu. Each function takes a tuple arg
// because that's how Fn() in TSL surfaces typed arguments.

import { Node } from 'three/webgpu';
import {
  Fn, vec2, vec3, float,
  fract, floor, sin, mix, dot
} from 'three/tsl';

// 2D hash — same formula LavaMaterial and GridMaterial already use.
// Classic `sin(dot(p, k)) * 43758...` trick; deterministic per 2D point.
export const hash2 = Fn((args: [Node<'vec2'>]) => {
  const [p] = args;
  return fract(sin(p.dot(vec2(127.1, 311.7))).mul(43758.5453));
});

// 3D hash returning a vec3 of uncorrelated values. Same formula
// SpaceMaterial / SkyMaterial use.
export const hash3 = Fn((args: [Node<'vec3'>]) => {
  const [p] = args;
  const q = vec3(
    p.dot(vec3(127.1, 311.7, 74.7)),
    p.dot(vec3(269.5, 183.3, 246.1)),
    p.dot(vec3(113.5, 271.9, 124.6))
  );
  return fract(sin(q).mul(43758.5453));
});

// 2D value noise ∈ [0,1]. Bilinear lerp of hash at four corners with
// Hermite (smoothstep) cubic weights. Same formula as LavaMaterial:38.
export const vnoise2 = Fn((args: [Node<'vec2'>]) => {
  const [p] = args;
  const i = floor(p).toVar();
  const f = fract(p).toVar();
  const u = f.mul(f).mul(float(3).sub(f.mul(2)));

  const a = hash2(i);
  const b = hash2(i.add(vec2(1, 0)));
  const c = hash2(i.add(vec2(0, 1)));
  const d = hash2(i.add(vec2(1, 1)));

  const x0 = mix(a, b, u.x);
  const x1 = mix(c, d, u.x);
  return mix(x0, x1, u.y);
});

// 2D FBM with compile-time octave count. TSL can't take a runtime loop
// count, but JS for-loops unroll cleanly into a flat node graph. Each
// octave doubles frequency (×lacunarity) and halves amplitude (×gain).
// Returns values roughly in [0,1] when gain≈0.5 and sum converges.
//
// Defaults match LavaMaterial's existing 5/2.02/0.52 baseline so the base
// flow still looks identical after the refactor.
export function fbm2(
  p: Node<'vec2'>,
  octaves = 5,
  lacunarity = 2.02,
  gain = 0.52
): Node<'float'> {
  return Fn(() => {
    const q = p.toVar();
    const amp = float(0.5).toVar();
    const sum = float(0).toVar();
    for (let i = 0; i < octaves; i++) {
      sum.addAssign(vnoise2(q).mul(amp));
      q.assign(q.mul(lacunarity).add(vec2(31.7, 17.3)));
      amp.assign(amp.mul(gain));
    }
    return sum;
  })();
}

// Analytic-derivative value noise (Quilez-style). Returns vec3(value, dF/dx, dF/dy).
// The value matches vnoise2 exactly; the partial derivatives let callers
// compute surface normals with a single evaluation instead of paying for
// three numerically-differenced samples.
//
// Derivation: the bilinear interpolation with cubic weights u=f²(3-2f) has
//   dF/df_x = du_x/df_x * ((b-a)(1-u_y) + (d-c)u_y)
//   dF/df_y = du_y/df_y * ((c-a)(1-u_x) + (d-b)u_x)
// where du/df = 6f(1-f). f = fract(p), so df/dp = 1 → those partials are
// already in p-space.
export const gradNoise2 = Fn((args: [Node<'vec2'>]) => {
  const [p] = args;
  const i = floor(p).toVar();
  const f = fract(p).toVar();
  const u = f.mul(f).mul(float(3).sub(f.mul(2)));
  // du/df = 6f(1-f)
  const du = f.mul(float(1).sub(f)).mul(6);

  const a = hash2(i);
  const b = hash2(i.add(vec2(1, 0)));
  const c = hash2(i.add(vec2(0, 1)));
  const d = hash2(i.add(vec2(1, 1)));

  const value = mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  const dFdx = du.x.mul(
    mix(b.sub(a), d.sub(c), u.y)
  );
  const dFdy = du.y.mul(
    mix(c.sub(a), d.sub(b), u.x)
  );
  return vec3(value, dFdx, dFdy);
});

// 2D Worley / cellular noise. Returns vec2(F1, F2-F1):
//   .x = distance to the nearest jittered point in the 3×3 cell neighborhood
//   .y = (distance to 2nd nearest) - (distance to nearest) = seam-distance
//        (0 at cell-wall boundaries, grows toward cell centres)
//
// Useful for plate/crust patterns: .x is small inside cells, .y is small at
// seams between cells. Nine hashes per evaluation — cheap enough to layer
// alongside fbm.
export const cell2 = Fn((args: [Node<'vec2'>]) => {
  const [p] = args;
  const pi = floor(p).toVar();
  const pf = fract(p).toVar();
  // Start both distances very large so any sampled point wins. Use squared
  // distance throughout so the inner loop avoids 9 sqrts; monotonic.
  const f1 = float(10).toVar();
  const f2 = float(10).toVar();
  for (let y = -1; y <= 1; y++) {
    for (let x = -1; x <= 1; x++) {
      const o = vec2(x, y);
      // Jittered feature point inside the neighbour cell. hash2 → [0,1]²
      // so the point never leaves its own cell (standard Worley layout).
      const r = hash2(pi.add(o));
      const diff = o.add(r).sub(pf);
      const d = dot(diff, diff).toVar();
      // Branchless F1/F2 update:
      //   newF1 = min(f1, d)
      //   newF2 = min(f2, max(f1, d))
      // Handles all three cases (d < f1 / f1 ≤ d < f2 / d ≥ f2) correctly.
      const oldF1 = f1.toVar();
      f1.assign(oldF1.min(d));
      f2.assign(f2.min(oldF1.max(d)));
    }
  }
  return vec2(f1.sqrt(), f2.sqrt().sub(f1.sqrt()));
});

// 2-layer IQ domain warp. Packages the technique LavaMaterial has been
// using inline: sample two fbm fields at time-animated offsets, use them
// as a warp vector, then sample a second fbm at the warped coordinate.
//
// Returns:
//   .x = final heat value (the doubly-warped fbm)
//   .yz = the warp vector q2 (useful downstream — passing it into later
//         noise layers makes them ride the warp instead of translating
//         independently, which is what makes layered flows look coherent).
//
// `scale` is applied to `p` inside; callers pass world coords directly.
export const domainWarp2 = Fn((args: [Node<'vec2'>, Node<'float'>, Node<'float'>]) => {
  const [p, t, scale] = args;
  const pp = p.mul(scale).toVar();

  const q1 = vec2(
    fbm2(pp.add(vec2(t, t.mul(0.3)))),
    fbm2(pp.add(vec2(t.mul(-0.7), t.mul(0.9))))
  );
  const q2 = vec2(
    fbm2(pp.add(q1).add(vec2(1.7, 9.2))),
    fbm2(pp.add(q1).add(vec2(8.3, 2.8)))
  );
  const f = fbm2(pp.add(q2.mul(2.4)).sub(vec2(t.mul(0.1), 0)));

  return vec3(f, q2.x, q2.y);
});
